import {
  useEffect,
  useState,
} from 'react';
import {
  Callout,
  Confirm,
  Field,
  FilterBar,
  Facts,
  Modal,
  Select,
  TextInput,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  formatDate,
  formatDay,
  useLoad,
  SortTh,
  Sorted,
  sortableCell,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import {
  affiliateActiveToggle,
} from '../../screens.ts';
import { t } from '../../messages.ts';
import { LoginAsButton } from '../login-as/LoginAs.tsx';
import { useAbilities } from '../abilities.tsx';
import { affiliateActions, affiliateEditActions, affiliateStanding, type AffiliateEdit, type LifecycleMode } from '../entity-actions.ts';
import { AffiliateEditDialog } from './AffiliateEdits.tsx';
import { LifecycleDialog } from '../LifecycleDialog.tsx';
import { ActionBar, Failure, OperatorFigures } from '../shared.tsx';
import { SuspendAffiliateModal } from './SuspendAffiliateModal.tsx';
import { AffiliateStatusModal } from './AffiliateStatusModal.tsx';
import { AffiliateInvoicesPanel, AffiliateMandatesPanel, AffiliateDisputesPanel } from './AffiliatePanels.tsx';

/**
 * Item 5 — everything an AMS row can offer about one affiliate: profile,
 * money, deal terms (revshare/CPA/tiers — what the user means by "terms"
 * here, not a legal agreement §6.8 keeps off the platform) and tracking
 * links. Same "whole picture" the operator's own console shows, read
 * cross-tenant and logged per access.
 */
export function AffiliateDetailModal({
  api,
  affiliateId,
  lockedBrand,
  profiles,
  onChanged,
  onClose,
}: {
  api: PlatformApi;
  affiliateId: string;
  /**
   * Every operator profile this person holds, when opened from the list of
   * people: the popup starts on `affiliateId` and its Operator picker moves
   * between these. Absent, or only one, and there is nothing to pick.
   */
  profiles?: readonly { affiliate_id: string; operator: string }[];
  /**
   * Opened from a brand: the dashboard and the money are this affiliate on
   * that brand alone, and the brand cannot be changed. Links, invoices,
   * disputes and terms still cover every brand. Opened from anywhere else,
   * the dashboard offers the brands they are enrolled on.
   */
  lockedBrand?: { brand_id: string; name: string };
  /** Told whenever a change lands here, so a list behind this can re-read. */
  onChanged?: () => void;
  onClose: () => void;
}) {
  // The profile on screen. Everything below reads this one, so moving the
  // Operator picker moves the whole popup — terms, money, links and all.
  const [current, setCurrent] = useState(affiliateId);
  const [version, setVersion] = useState(0);
  // Every write in this popup already bumps `version` to re-read itself, so
  // that one counter is also the moment to tell whoever opened it.
  useEffect(() => {
    if (version > 0) onChanged?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);
  const { ready, can } = useAbilities();
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const [changingIdentity, setChangingIdentity] = useState(false);
  const [identityReason, setIdentityReason] = useState('');
  // The narrowest of the three scopes, held here rather than in the row so
  // the row stays a row: which enrollment is being changed, and to what.
  const [changingBrand, setChangingBrand] = useState<{
    brandId: string;
    brand: string;
    to: 'ACTIVE' | 'SUSPENDED';
  } | null>(null);
  const [brandReason, setBrandReason] = useState('');
  const detail = useLoad(
    () => api.affiliateDetail(current, lockedBrand?.brand_id),
    [current, version, lockedBrand?.brand_id],
  );
  const links = useLoad(() => api.affiliateLinks(current), [current]);
  const standing = detail.state === 'ready'
    ? affiliateStanding({
        ...detail.value.affiliate,
        deleted_at: detail.value.deleted_at,
        purged_at: detail.value.purged_at,
      })
    : affiliateStanding({ status: '', identity_status: 'ACTIVE' });
  const actions = affiliateActions(standing, can);
  // 3.9d — direct edits, for a viewer holding the edit capability.
  const edits = affiliateEditActions(standing, can);
  const [editing, setEditing] = useState<AffiliateEdit | null>(null);
  /** What the last direct edit changed, shown until the next one or a profile switch. */
  const [edited, setEdited] = useState<string | null>(null);

  return (
    <Modal wide title={lockedBrand ? t('aff.detail.titleFromBrand', { brand: lockedBrand.name }) : t('common.affiliate')} onClose={onClose}>
      {profiles && profiles.length > 1 ? (
        <FilterBar>
          <Field
            label={t('common.operator')}
            hint={t('aff.detail.operatorHint')}
          >
            <Select
              value={current}
              onChange={(v) => {
                setCurrent(v);
                setChangingStatus(false);
                setChangingIdentity(false);
                setChangingBrand(null);
                setEditing(null);
                setEdited(null);
              }}
              options={profiles.map((p) => ({ value: p.affiliate_id, label: p.operator }))}
            />
          </Field>
        </FilterBar>
      ) : null}
      {detail.state === 'loading' ? <Loading rows={2} /> : null}
      {detail.state === 'error' ? <Failure error={detail.error} /> : null}
      {detail.state === 'ready' ? (
        <>
          {/*
            The name, the state it is in, and the one action that is not the
            state — all on one line.

            The separate Deactivate button is gone: the chip beside the name
            and that button were bound to the same fact (this person's state on
            this one operator), the chip printing the portal-facing word for it
            and the button changing it, so the chip is now the control. Pressing
            it opens the same confirmation the button opened — deactivating can
            be refused for a payable balance and asks for a reason on record, so
            it is a form and not a one-press toggle, and the chip goes on
            showing the state the row still has until that form has landed.

            `Suspend…` stays a button, and stays separate, because it is not
            the same fact: it asks what to reach — every operator this person
            holds a profile under, some of those operators, or some of their
            brands — and the answer can be wider than this one profile. One
            chip cannot stand for that choice.
          */}
          <div className="page-head">
            <h2>{detail.value.affiliate.legal_name}</h2>
            <AffiliateStateChip
              affiliate={detail.value.affiliate}
              canWrite={actions.includes('deactivate') || actions.includes('activate')}
              onChange={() => setChangingStatus(true)}
            />
            {standing.purged ? <Chip tone="overdue">{t('ent.state.purged')}</Chip>
              : standing.deleted ? <Chip tone="neutral">{t('ent.state.deleted')}</Chip> : null}
            {detail.value.affiliate.identity_status !== 'ACTIVE' ? (
              <Chip tone="overdue">{t('aff.detail.suspendedEverywhere')}</Chip>
            ) : null}
          </div>

          {ready ? (
            <ActionBar count={actions.length + edits.length} purged={standing.purged} can={can}>
              {edits.map((e) => (
                <button key={e} type="button" className="copy" onClick={() => { setEdited(null); setEditing(e); }}>
                  {e === 'edit-status' ? t('edit.aff.status')
                    : e === 'edit-profile' ? t('edit.aff.profile')
                      : e === 'edit-tags' ? t('edit.aff.tags')
                        : e === 'edit-manager' ? t('edit.aff.manager')
                          : t('edit.aff.note')}
                </button>
              ))}
              {actions.map((a) => {
                switch (a) {
                  case 'suspend-everywhere':
                  case 'reactivate-everywhere':
                    return (
                      <button
                        key={a}
                        type="button"
                        className={a === 'suspend-everywhere' ? 'danger-quiet' : 'copy'}
                        onClick={() => { setIdentityReason(''); setChangingIdentity(true); }}
                      >
                        {a === 'suspend-everywhere' ? t('aff.detail.suspend') : t('aff.detail.reactivateEverywhere')}
                      </button>
                    );
                  case 'login-as':
                    // §3.13 — the button checks the capability again itself.
                    return (
                      <LoginAsButton
                        key={a}
                        api={api}
                        operatorId={detail.value.operator_id}
                        kind="AFFILIATE"
                        name={detail.value.affiliate.legal_name}
                        affiliateId={current}
                      />
                    );
                  case 'delete':
                  case 'restore':
                  case 'purge':
                  case 'purge-now':
                    return (
                      <button
                        key={a}
                        type="button"
                        className={a === 'restore' ? 'copy' : 'danger-quiet'}
                        onClick={() => setLifecycle(a)}
                      >
                        {a === 'delete' ? t('ent.life.action.delete')
                          : a === 'restore' ? t('ent.life.action.restore')
                            : a === 'purge' ? t('ent.life.action.purge')
                              : t('ent.life.action.purgeNow')}
                      </button>
                    );
                  default:
                    // deactivate and activate are the state chip beside the name.
                    return null;
                }
              })}
            </ActionBar>
          ) : null}

          {edited ? <Callout fold={false}>{edited}</Callout> : null}

          {editing ? (
            <AffiliateEditDialog
              api={api}
              edit={editing}
              target={{
                operatorId: detail.value.operator_id,
                affiliateId: current,
                name: detail.value.affiliate.legal_name,
                status: detail.value.affiliate.status,
                fields: detail.value.affiliate,
                tags: (detail.value.affiliate.tags ?? []).map((tg) => tg.name),
                managers: detail.value.managers ?? [],
              }}
              onClose={() => setEditing(null)}
              onDone={(note) => {
                setEditing(null);
                setEdited(note);
                setVersion((v) => v + 1);
              }}
            />
          ) : null}

          {standing.deleted || standing.purged ? (
            <Callout>{standing.purged ? t('ent.affiliate.purgedNote') : t('ent.affiliate.deletedNote')}</Callout>
          ) : null}

          {lifecycle ? (
            <LifecycleDialog
              mode={lifecycle}
              kind="affiliate"
              name={detail.value.affiliate.legal_name}
              needsName={false}
              run={(body) =>
                api.affiliateDeletion(
                  detail.value.operator_id,
                  current,
                  lifecycle === 'purge-now' ? 'purge' : lifecycle,
                  { reason: body.reason },
                )}
              onClose={() => setLifecycle(null)}
              onDone={() => {
                setLifecycle(null);
                setVersion((v) => v + 1);
              }}
            />
          ) : null}

          {changingIdentity && detail.value.affiliate.identity_status === 'ACTIVE' ? (
            <SuspendAffiliateModal
              api={api}
              identityId={detail.value.affiliate.identity_id}
              name={detail.value.affiliate.legal_name}
              onClose={() => setChangingIdentity(false)}
              onDone={() => {
                setChangingIdentity(false);
                setVersion((v) => v + 1);
              }}
            />
          ) : null}

          {changingIdentity && detail.value.affiliate.identity_status !== 'ACTIVE' ? (
            <Confirm
              title={t('aff.detail.reactivateAllTitle')}
              tone="note"
              confirmLabel={t('aff.detail.reactivate')}
              body={
                <div className="stack">
                  <p>{t('aff.detail.reactivateAllBody')}</p>
                  <Field label={t('common.reason')}>
                    <TextInput value={identityReason} onChange={setIdentityReason} />
                  </Field>
                </div>
              }
              onCancel={() => setChangingIdentity(false)}
              onConfirm={async () => {
                await api.changeIdentityStatus(detail.value.affiliate.identity_id, 'ACTIVE', identityReason);
                setChangingIdentity(false);
                setVersion((v) => v + 1);
              }}
            />
          ) : null}

          {changingStatus ? (
            <AffiliateStatusModal
              api={api}
              operatorId={detail.value.operator_id}
              affiliateId={current}
              name={detail.value.affiliate.legal_name}
              from={detail.value.affiliate.status}
              onClose={() => setChangingStatus(false)}
              onDone={() => {
                setChangingStatus(false);
                setVersion((v) => v + 1);
              }}
            />
          ) : null}

          {/*
            The third scope, and the one the popup had no way to reach: this
            affiliate on one brand. `Confirm` and not `AffiliateStatusModal` —
            a payable balance does not block an enrollment change (it is the
            operator-wide status that owes money, not one brand's enrolment),
            so there is no refusal to negotiate and nothing to force past.

            Its sentence says "this brand only" for the same reason the name
            chip's says "every brand": with three scopes in one popup, an
            action that does not name its own reach is one somebody will read
            as the wrong one.
          */}
          {changingBrand ? (
            <Confirm
              title={
                changingBrand.to === 'SUSPENDED'
                  ? t('aff.detail.suspendOnBrandTitle', { brand: changingBrand.brand })
                  : t('aff.detail.reactivateOnBrandTitle', { brand: changingBrand.brand })
              }
              tone={changingBrand.to === 'SUSPENDED' ? 'error' : 'note'}
              confirmLabel={changingBrand.to === 'SUSPENDED' ? t('aff.suspend.submit') : t('aff.detail.reactivate')}
              body={
                <div className="stack">
                  <p>
                    {changingBrand.to === 'SUSPENDED'
                      ? t('aff.detail.suspendOnBrandBody', { brand: changingBrand.brand })
                      : t('aff.detail.reactivateOnBrandBody', { brand: changingBrand.brand })}
                  </p>
                  <Field label={t('common.reason')}>
                    <TextInput value={brandReason} onChange={setBrandReason} />
                  </Field>
                </div>
              }
              onCancel={() => setChangingBrand(null)}
              onConfirm={async () => {
                await api.setBrandEnrollmentStatus(
                  detail.value.operator_id,
                  current,
                  changingBrand.brandId,
                  changingBrand.to,
                  brandReason,
                );
                setChangingBrand(null);
                setVersion((v) => v + 1);
              }}
            />
          ) : null}

          <Facts
            items={[
              { label: t('common.email'), value: detail.value.affiliate.email },
              { label: t('common.country'), value: detail.value.affiliate.country },
              { label: t('aff.detail.factLocale'), value: detail.value.affiliate.locale },
              { label: t('aff.detail.factJoined'), value: formatDate(detail.value.affiliate.joined) },
              { label: t('aff.detail.factDormant'), value: detail.value.affiliate.is_dormant ? t('common.yes') : t('common.no') },
              ...(detail.value.affiliate.username
                ? [{ label: t('edit.profile.username'), value: detail.value.affiliate.username }]
                : []),
              {
                label: t('edit.fact.tags'),
                value: (detail.value.affiliate.tags ?? []).map((tg) => tg.name).join(', ') || '—',
              },
              {
                label: t('edit.fact.manager'),
                value: (detail.value.managers ?? [])
                  .map((m) => (m.role === 'PRIMARY' ? m.manager : `${m.manager} (${t('edit.manager.secondary')})`))
                  .join(', ') || '—',
              },
              ...(detail.value.affiliate.pending_reason
                ? [{ label: t('aff.detail.factPendingReason'), value: detail.value.affiliate.pending_reason }]
                : []),
            ]}
          />

          <AffiliateDashboardPanel
            // Keyed on the profile: its chosen brand belongs to one operator.
            key={current}
            api={api}
            affiliateId={current}
            brands={detail.value.brands}
            {...(lockedBrand ? { lockedBrand } : {})}
          />

          <Panel title={t('aff.enroll.title')}>
            {detail.value.brands.length === 0 ? (
              <Empty>{t('aff.enroll.empty')}</Empty>
            ) : (
              /* Item 6 (third batch) — a bare brand name read the same whether
                 the affiliate joined yesterday or was suspended on it last
                 month. Both facts travel with the row.

                 A table, like every other list in this console. As stacked
                 lines each enrolment read as one run of text — a name, a pill
                 and a date with nothing lining up between one row and the next
                 — and the question a reader brings here is a comparison down a
                 column: which of these is suspended, and which came first. The
                 shared scroll wrapper is what keeps it inside the popup. */
              <TableScroll>
                <table>
                  <thead>
                    <tr><th>{t('common.brand')}</th><th>{t('common.state')}</th><th className="num">{t('aff.enroll.colEnrolled')}</th></tr>
                  </thead>
                  <tbody>
                    {detail.value.brands.map((b) => (
                      <tr key={b.brand_id}>
                        <td>{b.brand}</td>
                        <td>
                          <BrandEnrollmentChip
                            enrollment={b}
                            canWrite={can.suspend && !standing.deleted && !standing.purged}
                            onChange={() => {
                              setBrandReason('');
                              setChangingBrand({
                                brandId: b.brand_id,
                                brand: b.brand,
                                to: b.state === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED',
                              });
                            }}
                          />
                        </td>
                        {/* §22.1 — `enrolled` is `to_char(enrolled_at, 'YYYY-MM-DD')`,
                            a calendar day already resolved by the server, not
                            an instant to convert to this device's zone. */}
                        <td className="num faint">{b.enrolled ? formatDay(b.enrolled) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </Panel>

          <AffiliateInvoicesPanel api={api} affiliateId={current} />
          <AffiliateDisputesPanel api={api} affiliateId={current} />

          <AffiliateMandatesPanel
            api={api}
            operatorId={detail.value.operator_id}
            affiliateId={current}
            canWrite={can.edit && !standing.deleted && !standing.purged}
          />

          <Panel title={t('aff.terms.title')}>
            {detail.value.terms.length === 0 ? (
              <Empty>{t('aff.terms.empty')}</Empty>
            ) : (
              <Sorted rows={detail.value.terms} by={{ brand: (t) => sortableCell(t.brand), commission_type: (t) => sortableCell(t.commission_type), revshare_rate_bps: (t) => sortableCell(t.revshare_rate_bps), cpa_amount_minor: (t) => sortableCell(t.cpa_amount_minor) }}>
                {(ordered, sort) => (
              <TableScroll>
                <table>
                  <thead>
                    <tr><SortTh sort={sort} col="brand">{t('common.brand')}</SortTh><SortTh sort={sort} col="commission_type">{t('aff.terms.colType')}</SortTh><SortTh sort={sort} col="revshare_rate_bps" align="num">{t('aff.terms.colRevshare')}</SortTh><SortTh sort={sort} col="cpa_amount_minor" align="num">{t('aff.terms.colCpa')}</SortTh></tr>
                  </thead>
                  <tbody>
                    {ordered.map((t) => (
                      <tr key={`${t.brand}-${t.commission_type}`}>
                        <td>{t.brand}</td>
                        <td>{t.commission_type}</td>
                        <td className="num">{t.revshare_rate_bps ?? '—'}</td>
                        <td className="num">{t.cpa_amount_minor ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
                )}
              </Sorted>
            )}
          </Panel>

          <Panel title={t('aff.money.title')}>
            <Facts
              items={Object.entries(detail.value.money).map(([k, v]) => ({
                label: k.replace(/_/g, ' '),
                value: String(v),
              }))}
            />
          </Panel>

          <Panel title={t('aff.links.title')}>
            {links.state === 'loading' ? <Loading rows={1} /> : null}
            {links.state === 'error' ? <Failure error={links.error} /> : null}
            {links.state === 'ready' ? (
              links.value.data.length === 0 ? (
                <Empty>{t('aff.links.empty')}</Empty>
              ) : (
                <Sorted rows={links.value.data} by={{ campaign: (l) => sortableCell(l.campaign), brand: (l) => sortableCell(l.brand), created: (l) => sortableCell(l.created), clicks: (l) => sortableCell(l.clicks), bot_clicks: (l) => sortableCell(l.bot_clicks), revoked: (l) => sortableCell(l.revoked) }}>
                  {(ordered, sort) => (
                <TableScroll>
                  <table>
                    <thead>
                      <tr>
                        <SortTh sort={sort} col="campaign">{t('aff.links.colCampaign')}</SortTh><SortTh sort={sort} col="brand">{t('common.brand')}</SortTh><SortTh sort={sort} col="created">{t('aff.links.colCreated')}</SortTh>
                        <SortTh sort={sort} col="clicks" align="num">{t('aff.links.colClicks')}</SortTh><SortTh sort={sort} col="bot_clicks" align="num">{t('aff.links.colBotClicks')}</SortTh><SortTh sort={sort} col="revoked">{t('aff.links.revoked')}</SortTh>
                      </tr>
                    </thead>
                    <tbody>
                      {ordered.map((l) => (
                        <tr key={l.link_id}>
                          <td>{l.campaign}</td>
                          <td className="faint">{l.brand}</td>
                          <td className="faint">{formatDate(l.created)}</td>
                          <td className="num">{l.clicks}</td>
                          <td className="num">{l.bot_clicks}</td>
                          <td>{l.revoked ? <Chip tone="overdue">{t('aff.links.revoked')}</Chip> : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
                  )}
                </Sorted>
              )
            ) : null}
          </Panel>

          <Callout>{detail.value.note}</Callout>
        </>
      ) : null}
    </Modal>
  );
}

/**
 * An affiliate's own dashboard — the operator's dashboard figures, narrowed to
 * this affiliate. Opened from a brand it is that brand's figures and nothing
 * else; opened from anywhere else it asks which of their brands to read,
 * starting on the first. Never "all brands" by default: someone who earned on
 * two is refused a blended NGR (§16.2), so a default of everything would open
 * on a refusal.
 */
function AffiliateDashboardPanel({
  api,
  affiliateId,
  brands,
  lockedBrand,
}: {
  api: PlatformApi;
  affiliateId: string;
  brands: readonly { brand_id: string; brand: string }[];
  lockedBrand?: { brand_id: string; name: string };
}) {
  const [chosen, setChosen] = useState(brands[0]?.brand_id ?? '');
  const brandId = lockedBrand?.brand_id ?? (chosen || undefined);
  const loaded = useLoad(() => api.affiliateAnalytics(affiliateId, brandId), [affiliateId, brandId]);

  return (
    <Panel
      title={t('aff.dash.title')}
      aside={
        lockedBrand ? (
          <span className="faint">{t('aff.dash.brandOnly', { brand: lockedBrand.name })}</span>
        ) : brands.length > 0 ? (
          <Select
            value={chosen}
            onChange={setChosen}
            options={brands.map((b) => ({ value: b.brand_id, label: b.brand }))}
          />
        ) : null
      }
    >
      {loaded.state === 'loading' ? <Loading rows={2} /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
      {loaded.state === 'ready' ? (
        <>
          {loaded.value.unavailable_because ? (
            <Callout tone="caution">{loaded.value.unavailable_because}</Callout>
          ) : null}
          {loaded.value.is_provisional ? (
            <Callout tone="caution">
              {loaded.value.provisional_because ?? t('aff.dash.provisional')}
            </Callout>
          ) : null}
          <OperatorFigures data={loaded.value.data} />
          <Callout>{t('aff.dash.asOf', { date: loaded.value.data_as_of })}</Callout>
        </>
      ) : null}
    </Panel>
  );
}

/**
 * An affiliate's state on one operator, as the control that changes it.
 *
 * A real `<button>`, on the same reasoning as the reference-data chip this is
 * modelled on: a `<span>` with an onClick is a control to a mouse and nothing
 * at all to a keyboard. The visible word is the state alone; the sentence
 * naming the state and then the action is the accessible name and the tooltip.
 *
 * Nothing here is optimistic. Pressing it opens the confirmation, and the chip
 * goes on saying what the affiliate's state still is until that write has
 * landed and the panel has re-read it. A refused deactivation — a payable
 * balance is the common one — leaves the chip exactly where it was, with the
 * refusal shown in the form that raised it.
 *
 * It stays an inert chip in the six states the action was never offered for.
 * Those are the operator's own to change, and a chip that looks pressable and
 * then refuses is worse than a fact that never claimed to be a control.
 */
function AffiliateStateChip({
  affiliate,
  canWrite,
  onChange,
}: {
  affiliate: { status: string; shown_to_affiliate: string };
  canWrite: boolean;
  onChange: () => void;
}) {
  const toggle = affiliateActiveToggle(affiliate);
  if (!canWrite || !toggle.canToggle) return <Chip tone={toggle.tone}>{toggle.label}</Chip>;
  return (
    <button
      type="button"
      className={`chip ${toggle.tone} ref-active-toggle`}
      aria-label={toggle.description}
      title={toggle.description}
      onClick={onChange}
    >
      {toggle.label}
    </button>
  );
}

/**
 * An affiliate's state on one brand, as the control that changes it.
 *
 * The same chip-is-the-button reasoning as `AffiliateStateChip` above, at the
 * scope one row down: the pill printed this enrolment's state and a reader
 * with a brand to stop had nowhere to press. Only two states reach here —
 * `affiliateOverview` filters WITHDRAWN out, and rightly, since withdrawing
 * is the affiliate's own act and not an operator's to reverse.
 *
 * The accessible name carries the brand, because "Active" alone is what four
 * rows in this table all say. Nothing is optimistic: the chip goes on saying
 * what the enrolment still is until the write has landed and the popup has
 * re-read it.
 */
function BrandEnrollmentChip({
  enrollment,
  canWrite,
  onChange,
}: {
  enrollment: { brand: string; state: string };
  canWrite: boolean;
  onChange: () => void;
}) {
  const suspended = enrollment.state === 'SUSPENDED';
  const tone = suspended ? 'overdue' : 'locked';
  const label = suspended ? t('aff.enroll.suspended') : t('common.active');
  if (!canWrite) return <Chip tone={tone}>{label}</Chip>;
  const description = suspended
    ? t('aff.enroll.hintSuspended', { brand: enrollment.brand })
    : t('aff.enroll.hintActive', { brand: enrollment.brand });
  return (
    <button
      type="button"
      className={`chip ${tone} ref-active-toggle`}
      aria-label={description}
      title={description}
      onClick={onChange}
    >
      {label}
    </button>
  );
}
